/**
 * @file VarianceDetailModal component
 * @description Modal showing baseline vs current date comparison and
 * change history since the baseline was set.
 */

import { useRoadmapItemHistory } from '@medsuite/api-client';

import { VarianceSummary } from './VarianceSummary';

import type { RoadmapItemWithInvestment } from '@medsuite/models';

interface VarianceDetailModalProps {
  item: RoadmapItemWithInvestment;
  onClose: () => void;
}

function formatDate(date: Date | string | null): string {
  if (!date) {
    return '-';
  }
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function toDateStr(date: Date | string | null): string {
  if (!date) {
    return '';
  }
  return new Date(date).toISOString().split('T')[0];
}

function daysDiff(a: string, b: string): number {
  const msPerDay = 86400000;
  const dateA = new Date(a);
  const dateB = new Date(b);
  if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
    return 0;
  }
  return Math.round((dateB.getTime() - dateA.getTime()) / msPerDay);
}

export function VarianceDetailModal({ item, onClose }: VarianceDetailModalProps) {
  const baselineDate = item.baseline_date ? toDateStr(item.baseline_date) : undefined;

  const { data: history = [], isLoading } = useRoadmapItemHistory(
    item.roadmap_item_id,
    baselineDate,
    { enabled: !!baselineDate }
  );

  const baselineStartStr = toDateStr(item.baseline_start_date);
  const baselineEndStr = toDateStr(item.baseline_end_date);
  const currentStartStr = toDateStr(item.start_date);
  const currentEndStr = toDateStr(item.end_date);

  const netVariance = baselineEndStr && currentEndStr
    ? daysDiff(baselineEndStr, currentEndStr)
    : 0;

  const netColorClass = netVariance > 0
    ? 'text-red-600 dark:text-red-400'
    : netVariance < 0
      ? 'text-green-600 dark:text-green-400'
      : 'text-gray-500 dark:text-gray-400';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Schedule Variance
            </h2>
            <button
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Title */}
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {item.investment_title || `Item #${item.roadmap_item_id}`}
            </div>

            {/* Side-by-side comparison */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="font-medium text-gray-500 dark:text-gray-400" />
              <div className="font-medium text-gray-500 dark:text-gray-400 text-center">
                Baseline
              </div>
              <div className="font-medium text-gray-500 dark:text-gray-400 text-center">
                Current
              </div>

              <div className="text-gray-700 dark:text-gray-300">Start</div>
              <div className="text-center text-gray-900 dark:text-gray-100">
                {formatDate(item.baseline_start_date)}
              </div>
              <div className="text-center text-gray-900 dark:text-gray-100">
                {formatDate(item.start_date)}
              </div>

              <div className="text-gray-700 dark:text-gray-300">End</div>
              <div className="text-center text-gray-900 dark:text-gray-100">
                {formatDate(item.baseline_end_date)}
              </div>
              <div className="text-center text-gray-900 dark:text-gray-100">
                {formatDate(item.end_date)}
              </div>
            </div>

            {/* Net variance */}
            <div className="flex items-center justify-between rounded-md bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Net Variance
              </span>
              <span className={`text-sm font-semibold ${netColorClass}`}>
                {netVariance > 0 ? `+${netVariance}` : netVariance} days
              </span>
            </div>

            {/* Inline variance summary */}
            {baselineStartStr && baselineEndStr && (
              <VarianceSummary
                baselineStart={baselineStartStr}
                baselineEnd={baselineEndStr}
                currentStart={currentStartStr}
                currentEnd={currentEndStr}
              />
            )}

            {/* Baseline set date */}
            {item.baseline_date && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Baseline set: {formatDate(item.baseline_date)}
              </div>
            )}

            {/* Change history */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Change History
              </h3>
              {isLoading ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">Loading history...</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No changes since baseline.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="py-1 pr-3 text-left font-medium text-gray-500 dark:text-gray-400">
                          Field
                        </th>
                        <th className="py-1 pr-3 text-left font-medium text-gray-500 dark:text-gray-400">
                          Old
                        </th>
                        <th className="py-1 pr-3 text-left font-medium text-gray-500 dark:text-gray-400">
                          New
                        </th>
                        <th className="py-1 pr-3 text-left font-medium text-gray-500 dark:text-gray-400">
                          Date
                        </th>
                        <th className="py-1 text-left font-medium text-gray-500 dark:text-gray-400">
                          By
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((entry, idx) => (
                        <tr
                          key={idx}
                          className="border-b border-gray-100 dark:border-gray-700/50"
                        >
                          <td className="py-1 pr-3 text-gray-700 dark:text-gray-300">
                            {entry.field_changed}
                          </td>
                          <td className="py-1 pr-3 text-gray-600 dark:text-gray-400">
                            {entry.old_value || '-'}
                          </td>
                          <td className="py-1 pr-3 text-gray-900 dark:text-gray-100">
                            {entry.new_value || '-'}
                          </td>
                          <td className="py-1 pr-3 text-gray-600 dark:text-gray-400">
                            {formatDate(entry.changed_at)}
                          </td>
                          <td className="py-1 text-gray-600 dark:text-gray-400">
                            {entry.changed_by || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <button
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VarianceDetailModal;
